import React, {useRef, useCallback, useEffect, useMemo, useState} from 'react';
import {connect} from "react-redux";
import CustomSnackbar from "../alert/CustomSnackbar";
import DataGrid from "react-data-grid";
import {
    CustomTableHeaderCellRender,
    EmptyRowsRenderer,
    GetListOfSelectedRowIDs,
    GetSelectedGroupByPlaceholderFromTrafficSource,
    GetSelectedRowID, HandleCopyNames,
    IsAtBottom
} from "../reporting-table/ReactDataGridUtils";
import {debounce} from "lodash";
import Spinner from "../spinner/Spinner";
import {
    StatusColumn,
    MarkColumn,
    DynamicGridColumnCreator, CustomSelectRowColumn
} from "../reporting-table/Columns";
import {groupBy1Action} from "../../store/actions/groupBy1Action";
import DetailPanelToolbar from "../reporting-table/DetailPanelToolbar";
import {alertActions} from "../../store/actions/alertActions";
import {columnActions} from "../../store/actions/columnAction";
import {workspaceActions} from "../../store/actions/workspaceAction";
import {Divider, useTheme} from "@mui/material";
import useCustomStyles from "../utils/UseCustomStyles";
import {DetailsCampaignDataGridBox} from "./CampaignsView";

const customStyles = (theme) => ({
    dataGrid: theme.defaultDataGrid
});

function GroupBy1Grid(props) {
    const theme = useTheme();
    const classes = useCustomStyles(customStyles, theme);
    const {
        alert,
        loadDetailsReport,
        updateSortBy,
        updateOrderBy,
        updateSortColumns,
        setSelectedRows,
        getIntegrationData,
        findTrafficSourceAndIntegrationByTrafficSourceId,
        refresh,
        updateStatus,
        updateBid,
        updateBudget,
        errorMsg,
        successMsg,
        columnGroup,
        report,
        getWorkspaceIDs,
    } = props;

    const {
        data, isLoading, orderBy, sortBy, sortColumns, rerender, groupBy, selectedRows, selectedUUIDs, integration,
        trafficSource
    } = props.groupBy1State;

    let notAllowedMetricColumnsForPostbackParamReport = ["metric_uniqueVisits", "metric_uniqueClicks", "metric_botVisits", "metric_botClicks", "metric_visits", "metric_clicks", "metric_cpv", "metric_cpc", "metric_ctr", "metric_uctr", "metric_cr", "metric_cv", "metric_roi", "metric_epc", "metric_epv"];
    let allowedPostbackParamReportGroupByValues = ["postback_param1", "postback_param2", "postback_param3", "postback_param4", "postback_param5"];
    let notAllowedColumnsForPostbackParamReport = ["uniqueVisits", "uniqueClicks", "botVisits", "botClicks", "visits", "clicks", "cpv", "cpc", "ctr", "uctr", "cr", "cv", "roi", "epc", "epv"];

    const {from, to, isDeleted} = props.ddToolbarState;
    const {timezone, currencyCode} = props.auth.userSettings;

    const workspaceIDs = (props?.workspace?.userWorkspaces || [])
        .filter(workspace => workspace?.selected && workspace?.workspaceId)
        .map(workspace => workspace.workspaceId).join(",");
    const workspacesReady = workspaceIDs !== "";

    const gridRef = useRef(null);
    const lastScrollTop = useRef(0);
    const [columns, setColumns] = useState(null);

    const updateColumnResize = (idx, width) => {
        props.updateColumnWidth({columnKey: columns[idx]?.key, width, columnGroup, report});
    };

    const modifiedTableColumns = useMemo(() => {
        function CustomHeaderCell(props) {
            return <CustomTableHeaderCellRender {...props}/>;
        }

        return columns?.map((c) => {
            if (c?.key === "custom-select-row" || c?.key === "mark") {
                return c;
            }
            return {...c, renderHeaderCell: CustomHeaderCell};
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columns, columnGroup, report]);

    const getTableColumns = () => {
        let columns = [];
        props?.column?.data[columnGroup]?.forEach((col) => {
            if (allowedPostbackParamReportGroupByValues.includes(groupBy?.groupBy)) {
                if (!col?.hidden && !notAllowedMetricColumnsForPostbackParamReport.includes(col?.reportColumnKey)) {
                    columns.push(col?.reportColumnKey);
                }
            } else {
                if (!col?.hidden) {
                    columns.push(col?.reportColumnKey);
                }
            }
        });
        return columns;
    };

    const loadMore = (pageNumber) => {
        if (!workspacesReady) {
            return;
        }

        let params = {
            groupBy: groupBy?.groupBy,
            from: from,
            to: to,
            timezone: timezone,
            orderBy: orderBy,
            sortBy: sortBy,
            page: pageNumber,
            pageSize: 100,
            currencyCode: currencyCode,
            columns: getTableColumns(),
            workspace: getWorkspaceIDs()
        };

        // Filter by selected campaign ids
        if (props?.campaignsState?.selectedRows.size > 0) {
            params[`filters[table_campaign_ids]`] = GetListOfSelectedRowIDs(props?.campaignsState?.selectedRows);
        }

        // Filter by selected traffic source ids
        if (props?.campaignsState?.selectedRows.size === 0) {
            let trafficSources = [];
            if (props?.campaignsState?.filterTrafficSources !== undefined) {
                for (const filter of props?.campaignsState?.filterTrafficSources) {
                    trafficSources.push(filter?.value?.toString());
                }
                params[`filters[table_traffic_source_ids]`] = trafficSources.join(",");
            }
        }

        // Add metric filters
        if (props?.metricFilter?.filters?.length > 0) {
            // eslint-disable-next-line array-callback-return
            props?.metricFilter?.filters?.map((obj) => {
                for (const metric in obj) {
                    params[`metricFilters[${metric}]`] = obj[metric];
                }
            });
        }

        loadDetailsReport(params, true);
    };

    const getDetailsReport = ({exportPage, exportPageSize, exportData}) => {
        if (!workspacesReady) {
            return;
        }
        lastScrollTop.current = 0;

        gridRef?.current?.element?.scrollTo(0, 0);

        let params = {
            groupBy: groupBy?.groupBy,
            from: from,
            to: to,
            timezone: timezone,
            orderBy: orderBy,
            sortBy: sortBy,
            page: 1,
            pageSize: 100,
            currencyCode: currencyCode,
            columns: getTableColumns(),
            workspace: getWorkspaceIDs()
        };

        // Filter by selected campaign ids
        if (props?.campaignsState?.selectedRows?.size > 0) {
            params[`filters[table_campaign_ids]`] = GetListOfSelectedRowIDs(props?.campaignsState?.selectedRows);
        }

        // Filter by selected traffic source ids
        if (props?.campaignsState?.selectedRows?.size === 0) {
            let trafficSources = [];
            if (props?.campaignsState?.filterTrafficSources !== undefined) {
                for (const filter of props?.campaignsState?.filterTrafficSources) {
                    trafficSources.push(filter?.value?.toString());
                }
                params[`filters[table_traffic_source_ids]`] = trafficSources.join(",");
            }
        }

        // Export CSV data
        if (exportData === "csv") {
            params.page = exportPage;
            params.pageSize = exportPageSize;
            params.export = exportData;
        }

        // Add metric filters
        if (props?.metricFilter?.filters?.length > 0) {
            // eslint-disable-next-line array-callback-return
            props?.metricFilter?.filters?.map((obj) => {
                for (const metric in obj) {
                    params[`metricFilters[${metric}]`] = obj[metric];
                }
            });
        }

        loadDetailsReport(params);
    };

    const handleInfinityScroll = (event) => {

        const {scrollTop} = event.currentTarget;
        const scrollingDown = scrollTop > lastScrollTop.current;
        lastScrollTop.current = scrollTop;

        if (!scrollingDown || isLoading || props.groupBy1State.errorMsg || !workspacesReady || !IsAtBottom(event)) {
            return;
        }

        const hasMoreRows = data?.page > 0 && data?.recordsPerPage > 0 &&
            data.page < Math.ceil(data?.total?.totalRecords / data.recordsPerPage);
        if (hasMoreRows) {
            loadMore(data.page + 1);
        }
    };

    const handleRefresh = () => {
        if (!workspacesReady || isLoading) {
            return;
        }
        lastScrollTop.current = 0;
        gridRef?.current?.element?.scrollTo(0, 0);
        refresh();
    };

    const handleExportCSV = () => {
        getDetailsReport({exportPage: 1, exportPageSize: 100000, exportData: "csv"});
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleColumnResize = useCallback(debounce(updateColumnResize, 500), [columns, columnGroup, report]);

    const handleColumnsRender = (
        {
            addDeliveryStatusColumn = false,
            addActionColumn = false,
            addBidColumn = false,
            addFbMetricCpmColumn = false,
            addFbMetricLinkCpcColumn = false,
            addFbMetricLinkCtrColumn = false,
            addFbMetricLinkClicksColumn = false,
            addFbMetricEpcColumn = false,
            addFbImpressionsColumn = false,
            addFbDailyBudgetColumn = false,
        }
    ) => {

        let columns = [
            CustomSelectRowColumn({setSelectedRows}),
            MarkColumn,
            ...DynamicGridColumnCreator({
                columns: props?.column?.data[columnGroup],
                addBidColumn,
                addDeliveryStatusColumn,
                addFbMetricCpmColumn,
                addFbMetricLinkCpcColumn,
                addFbMetricLinkCtrColumn,
                addFbMetricLinkClicksColumn,
                addFbMetricEpcColumn,
                addFbImpressionsColumn,
                addFbDailyBudgetColumn,
                handleChangeDailyBudget: updateBudget,
                handleChangeExternalBid: updateBid,
            })
        ];
        // Add action columns.
        if (addActionColumn) {
            columns.splice(3, 0, StatusColumn(updateStatus));
        }

        if (allowedPostbackParamReportGroupByValues.includes(groupBy?.groupBy)) {
            // Remove columns for Postback Param Report in Details view.
            // eslint-disable-next-line array-callback-return
            let d = columns.filter((item) => {
                if (!notAllowedColumnsForPostbackParamReport.includes(item?.key)) {
                    return item;
                }
            });
            setColumns(d);
        } else {
            setColumns(columns);
        }
    };

    const applyIntegrationActionColumns = () => {
        let listOfIntegrationMacros = [
            {networkType: "pushground", groupByPlaceholder: "{campaign_id}", changeStatus: true, changeBid: true},
            {networkType: "exoclick", groupByPlaceholder: "{campaign_id}", changeStatus: true, changeBid: true},
            {networkType: "propeller-ads", groupByPlaceholder: "{campaignid}", changeStatus: true, changeBid: true},
            {networkType: "mondiad", groupByPlaceholder: "[campaignid]", changeStatus: true, changeBid: true},
            {networkType: "mondiad", groupByPlaceholder: "[creativeid]", changeStatus: true, changeBid: false},
            {networkType: "google", groupByPlaceholder: "{campaignid}", changeStatus: true, changeBid: false},
            {networkType: "google", groupByPlaceholder: "{adgroupid}", changeStatus: true, changeBid: false},
            {networkType: "google", groupByPlaceholder: "{creative}", changeStatus: true, changeBid: false},
            {networkType: "microsoft", groupByPlaceholder: "{CampaignId}", changeStatus: true, changeBid: false},
            {networkType: "microsoft", groupByPlaceholder: "{AdGroupId}", changeStatus: true, changeBid: false},
            {networkType: "microsoft", groupByPlaceholder: "{AdId}", changeStatus: true, changeBid: false},
            {networkType: "facebook", groupByPlaceholder: "{{ad.id}}", changeStatus: true, dailyBudget: true, deliveryStatus: true, metricImpressions: true, metricCPM: true, metricLinkCPC: true, metricLinkCTR: true, metricLinkClicks: true, metricEpc: true,},
            {networkType: "facebook", groupByPlaceholder: "{{adset.id}}", changeStatus: true, dailyBudget: true, changeBid: true, deliveryStatus: true, metricImpressions: true, metricCPM: true, metricLinkCPC: true, metricLinkCTR: true, metricLinkClicks: true, metricEpc: true},
            {networkType: "facebook", groupByPlaceholder: "{{campaign.id}}", changeStatus: true, dailyBudget: true, deliveryStatus: true, metricImpressions: true, metricCPM: true, metricLinkCPC: true, metricLinkCTR: true, metricLinkClicks: true, metricEpc: true,},
            {networkType: "taboola", groupByPlaceholder: "{campaign_id}", changeStatus: true, changeBid: false, dailyBudget: true, deliveryStatus: true},
            {networkType: "taboola", groupByPlaceholder: "{campaign_item_id}", changeStatus: false, changeBid: false},
            {networkType: "mgid", groupByPlaceholder: "{campaign_id}", changeStatus: true, changeBid: false, deliveryStatus: true},
            {networkType: "mgid", groupByPlaceholder: "{teaser_id}", changeStatus: true, changeBid: true, deliveryStatus: true},
            {networkType: "outbrain", groupByPlaceholder: "{{campaign_id}}", changeStatus: true, changeBid: true, dailyBudget: true, deliveryStatus: true},
            {networkType: "tiktok", groupByPlaceholder: "__CAMPAIGN_ID__", changeStatus: true, dailyBudget: true, deliveryStatus: true, metricImpressions: true, metricCPM: true, metricLinkCPC: true, metricLinkCTR: true, metricLinkClicks: true, metricEpc: true,},
            {networkType: "tiktok", groupByPlaceholder: "__AID__", changeStatus: true, dailyBudget: true, deliveryStatus: true, metricImpressions: true, metricCPM: true, metricLinkCPC: true, metricLinkCTR: true, metricLinkClicks: true, metricEpc: true,},
            {networkType: "tiktok", groupByPlaceholder: "__CID__", changeStatus: true, dailyBudget: false, deliveryStatus: true, metricImpressions: true, metricCPM: true, metricLinkCPC: true, metricLinkCTR: true, metricLinkClicks: true, metricEpc: true,},
            {networkType: "revcontent", groupByPlaceholder: "{boost_id}", changeStatus: true, changeBid: true, dailyBudget: true, deliveryStatus: true},
            {networkType: "outbrain_dsp", groupByPlaceholder: "{campaignid}", changeStatus: false, changeBid: false, dailyBudget: false, deliveryStatus: true},
            {networkType: "outbrain_dsp", groupByPlaceholder: "{adgroupid}", changeStatus: true, changeBid: true, dailyBudget: true, deliveryStatus: true},
            {networkType: "newsbreak", groupByPlaceholder: "__CAMPAIGN_ID__", changeStatus: true, changeBid: false, dailyBudget: false, deliveryStatus: true},
            {networkType: "newsbreak", groupByPlaceholder: "__FLIGHT_ID__", changeStatus: true, changeBid: true, dailyBudget: true, deliveryStatus: true},
            {networkType: "newsbreak", groupByPlaceholder: "__CREATIVE_ID__", changeStatus: true, changeBid: false, dailyBudget: false, deliveryStatus: true},
            {networkType: "smartnews", groupByPlaceholder: "{campaign_id}", changeStatus: true, dailyBudget: true, deliveryStatus: true, metricImpressions: true, metricCPM: true, metricLinkCPC: true, metricLinkCTR: true, metricLinkClicks: true, metricEpc: true,},
            {networkType: "smartnews", groupByPlaceholder: "{ad_group_id}", changeStatus: true, dailyBudget: false, deliveryStatus: true, metricImpressions: true, metricCPM: true, metricLinkCPC: true, metricLinkCTR: true, metricLinkClicks: true, metricEpc: true,},
            {networkType: "smartnews", groupByPlaceholder: "{ad_id}", changeStatus: true, dailyBudget: false, deliveryStatus: true, metricImpressions: true, metricCPM: true, metricLinkCPC: true, metricLinkCTR: true, metricLinkClicks: true, metricEpc: true,},
            // eslint-disable-next-line no-template-curly-in-string
            {networkType: "mediago", groupByPlaceholder: "${CAMPAIGN_ID}", changeStatus: true, dailyBudget: true, deliveryStatus: true, metricImpressions: true, metricCPM: false, metricLinkCPC: false, metricLinkCTR: false, metricLinkClicks: true, metricEpc: true, changeBid: true},
            // eslint-disable-next-line no-template-curly-in-string
            {networkType: "popin", groupByPlaceholder: "${CAMPAIGN_ID}", changeStatus: false, dailyBudget: false, deliveryStatus: true, metricImpressions: true, metricCPM: false, metricLinkCPC: false, metricLinkCTR: false, metricLinkClicks: true, metricEpc: true, changeBid: false},
        ];

        let addDeliveryStatusColumn = false;
        let addFbDailyBudgetColumn = false;
        let addFbImpressionsColumn = false;
        let addFbMetricEpcColumn = false;
        let addFbMetricCpmColumn = false;
        let addFbMetricLinkCpcColumn = false;
        let addFbMetricLinkCtrColumn = false;
        let addFbMetricLinkClicksColumn = false;
        let addBidColumn = false;
        let addActionColumn = false;
        for (const int of listOfIntegrationMacros) {
            if (integration?.networkType === int?.networkType) {
                let groupByPlaceholder = GetSelectedGroupByPlaceholderFromTrafficSource(groupBy?.groupBy, trafficSource);
                if (groupByPlaceholder === int?.groupByPlaceholder) {
                    addDeliveryStatusColumn = int?.deliveryStatus;
                    addActionColumn = int?.changeStatus;
                    addBidColumn = int?.changeBid;
                    addFbDailyBudgetColumn = int?.dailyBudget;
                    addFbImpressionsColumn = int?.metricImpressions;
                    addFbMetricCpmColumn = int?.metricCPM;
                    addFbMetricLinkCpcColumn = int?.metricLinkCPC;
                    addFbMetricLinkCtrColumn = int?.metricLinkCTR;
                    addFbMetricLinkClicksColumn = int?.metricLinkClicks;
                    addFbMetricEpcColumn = int?.metricEpc;
                    break;
                }
            }
        }
        handleColumnsRender({
            addActionColumn,
            addBidColumn,
            addFbDailyBudgetColumn,
            addDeliveryStatusColumn,
            addFbImpressionsColumn,
            addFbMetricCpmColumn,
            addFbMetricLinkCpcColumn,
            addFbMetricLinkCtrColumn,
            addFbMetricLinkClicksColumn,
            addFbMetricEpcColumn
        });
    };

    // Step 1: Get traffic source and integration data and set default columns if not integration.
    useEffect(() => {
        if (props?.campaignsState?.selectedRows?.size === 1) {
            const campaignId = GetSelectedRowID(props?.campaignsState?.selectedRows);

            let isIntegrated = false;
            props?.campaignsState?.data?.records.forEach(c => {
                if (c?.id === campaignId) {
                    isIntegrated = c?.isIntegrated;
                }
            });

            if (!isIntegrated) {
                handleColumnsRender({});
                getDetailsReport({});
                return () => {
                };
            }

            if (isIntegrated && integration === null) {
                getIntegrationData(campaignId);
                return () => {
                };
            }
        } else if (props?.campaignsState?.filterTrafficSources?.length === 1 && integration === null) {
            findTrafficSourceAndIntegrationByTrafficSourceId();

            let isIntegrated = false;
            props?.campaignsState?.data?.records.forEach(c => {
                isIntegrated = c?.isIntegrated;
            });

            // Add extra check if there is no integration for listed campaign list. Otherwise is won't load any data.
            if (!isIntegrated) {
                handleColumnsRender({});
                getDetailsReport({});
                return () => {
                };
            }

        } else {
            handleColumnsRender({});
            getDetailsReport({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupBy, findTrafficSourceAndIntegrationByTrafficSourceId, getIntegrationData, props?.campaignsState?.data?.records, props?.campaignsState?.selectedRows]);

    // Step 2: If integration and traffic source data has found.
    // Then match "Group by 1" value with traffic source placeholder.
    useEffect(() => {
        if (integration !== null) {
            applyIntegrationActionColumns();
            getDetailsReport({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupBy, integration])

    // Step 3: Load details report data.
    useEffect(() => {
        if (columns !== null) {
            getDetailsReport({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workspacesReady, workspaceIDs, from, to, timezone, isDeleted, sortBy, orderBy, currencyCode, props?.metricFilter?.filters]);

    // Reload details report data if rerender is asked.
    useEffect(() => {
        if (rerender && columns !== null) {
            getDetailsReport({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rerender]);

    // Rerender columns if width, visibility or sequence has changed.
    useEffect(() => {
        if (columns !== null) {
            applyIntegrationActionColumns();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props?.column?.data]);

    // Set OrderBy and SortBy
    useEffect(() => {
        if (sortColumns.length === 0) {
            updateSortBy("");
            updateOrderBy("");
            return () => {
            };
        }
        const {columnKey, direction} = sortColumns[0];
        updateOrderBy(columnKey);
        updateSortBy(direction);
    }, [updateOrderBy, updateSortBy, sortColumns]);

    // 6. Rerender if user workspaces has changed.
    useEffect(() => {
        if (props?.workspace?.updated !== null) {
            props?.resetUpdatedWorkspaceValue();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props?.workspace?.updated]);

    // 7. Force to rerender selected rows...
    useEffect(() => {
        if (selectedUUIDs?.size > 0) {
            let newUUIDs = new Set();
            for (const s of selectedRows) {
                data?.records?.forEach(i => {
                    if (i?.name === s) {
                        newUUIDs.add(i?.uuid)
                    }
                })
            }
            setSelectedRows(newUUIDs, selectedRows);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props?.groupBy1State?.data?.records]);

    // Using row ID as a value for these values.
    let a = ["campaign_id", "offer_id", "landing_id", "flow_id", "affiliate_network_id", "traffic_source_id"];

    return (
        <>
            <DetailPanelToolbar
                selectedCount={selectedRows.size}
                campaignId={props?.campaignsState?.selectedRows?.size === 1 ? GetSelectedRowID(props?.campaignsState?.selectedRows) : 0}
                onClickRefresh={handleRefresh}
                onClickExportCSV={handleExportCSV}
                onClickCopyNames={() => HandleCopyNames({selectedRows, errorMsg, successMsg})}
                report={report}
                columns={columns}
                columnGroup={columnGroup}
            />
            <Divider/>
            <DetailsCampaignDataGridBox>

                {alert.errorMsg !== "" && <CustomSnackbar message={alert.errorMsg} timeout={3000}/>}
                {alert.successMsg !== "" &&
                    <CustomSnackbar message={alert.successMsg} severity="success" timeout={3000}/>}

                {isLoading && <Spinner overComponentBox={true}/>}

                {columns !== null && classes !== null &&
                    <DataGrid
                        ref={gridRef}
                        rowKeyGetter={(row) => {
                            return row?.uuid;
                        }}
                        columns={modifiedTableColumns}
                        rows={props?.groupBy1State?.data?.records ? props?.groupBy1State?.data?.records : []}
                        defaultColumnOptions={{
                            sortable: true,
                            resizable: true,
                            draggable: false
                        }}
                        onColumnResize={handleColumnResize}
                        sortColumns={sortColumns}
                        onSortColumnsChange={updateSortColumns}
                        selectedRows={selectedUUIDs}
                        onSelectedRowsChange={(uuids) => {
                            let realIds = new Set();
                            for (const s of uuids) {
                                data?.records?.forEach(i => {
                                    if (i?.uuid === s) {
                                        if (i?.id > 0 && a.includes(props?.groupBy1State?.groupBy?.groupBy)) {
                                            realIds.add(i?.id);
                                        } else {
                                            realIds.add(i?.name);
                                        }
                                    }
                                })
                            }
                            setSelectedRows(uuids, realIds);
                        }}
                        onCellClick={(args) => {

                            // disable row select for these columns.
                            if (args?.column?.key === "status" || args?.column?.key === "external_bid" || args?.column?.key === "fb_daily_budget") {
                                return false;
                            }

                            if (args?.column?.key !== "custom-select-row") {
                                if (props.groupBy2State.selectedRows?.size > 0) {
                                    props.groupBy2State.selectedUUIDs.clear();
                                    props.groupBy2State.selectedRows.clear();
                                }

                                if (args?.row?.id > 0 && a.includes(props?.groupBy1State?.groupBy?.groupBy)) {
                                    setSelectedRows(new Set().add(args?.row?.uuid), new Set().add(args?.row?.id));
                                } else {
                                    setSelectedRows(new Set().add(args?.row?.uuid), new Set().add(args?.row?.name));
                                }
                            }
                        }}
                        onSelectedCellChange={({rowIdx, row, column}) => {
                            // disable row select for these columns.
                            if (column?.key === "status" || column?.key === "external_bid" || column?.key === "fb_daily_budget") {
                                return false;
                            }

                            if (rowIdx !== -1 && column?.key !== "custom-select-row") {
                                if (props.groupBy2State.selectedRows?.size > 0) {
                                    props.groupBy2State.selectedUUIDs.clear();
                                    props.groupBy2State.selectedRows.clear();
                                }

                                if (row?.id > 0 && a.includes(props?.groupBy1State?.groupBy?.groupBy)) {
                                    setSelectedRows(new Set().add(row?.uuid), new Set().add(row?.id));
                                } else {
                                    setSelectedRows(new Set().add(row?.uuid), new Set().add(row?.name));
                                }
                            }
                        }}
                        onCellDoubleClick={({row, column}) => {
                            if (column?.key === "mark" || column?.key === "custom-select-row" || column?.key === "status" || column?.key === "external_bid" || column?.key === "fb_daily_budget") {
                                return false;
                            }
                            props.handleDoubleClick(row, column);
                        }}
                        onScroll={handleInfinityScroll}
                        noRowsFallback={<EmptyRowsRenderer/>}
                        summaryRowHeight={35}
                        bottomSummaryRows={[data]}
                        enableVirtualization={true}
                        className={classes?.dataGrid}
                    />
                }
            </DetailsCampaignDataGridBox>
        </>
    );
}

const mapStateToProps = (state) => {
    return {
        campaignsState: state.campaign,
        groupBy1State: state.groupBy1,
        groupBy2State: state.groupBy2,
        ddToolbarState: state.ddToolbar,
        auth: state.auth,
        alert: state.alert,
        metricFilter: state.metricFilter,
        column: state.column,
        workspace: state.workspace
    }
};

const actionCreators = {
    loadDetailsReport: groupBy1Action.getDetailsReport,
    updateOrderBy: groupBy1Action.updateOrderBy,
    updateSortBy: groupBy1Action.updateSortBy,
    updateSortColumns: groupBy1Action.updateSortColumns,
    setSelectedRows: groupBy1Action.setSelectedRows,
    getIntegrationData: groupBy1Action.getIntegrationData,
    findTrafficSourceAndIntegrationByTrafficSourceId: groupBy1Action.findTrafficSourceAndIntegrationByTrafficSourceId,
    refresh: groupBy1Action.rerender,
    updateStatus: groupBy1Action.updateStatus,
    updateBid: groupBy1Action.updateBid,
    updateBudget: groupBy1Action.updateBudget,
    errorMsg: alertActions.error,
    successMsg: alertActions.success,
    updateColumnWidth: columnActions.updateColumnWidth,
    getWorkspaceIDs: workspaceActions.getWorkspaceIDs,
    resetUpdatedWorkspaceValue: workspaceActions.resetUpdatedWorkspaceValue
};

export default connect(mapStateToProps, actionCreators)(GroupBy1Grid);